/**
 * Bunny & Red — Find My Size + Fit Guarantee
 * Viewport-level modals via native <dialog>.showModal() (top layer).
 * Height/weight stay client-side only.
 */
(function () {
  'use strict';

  const IN_TO_CM = 2.54;
  const LB_TO_KG = 0.45359237;
  let scrollLockCount = 0;
  let savedScrollY = 0;

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function parseJSON(el, attr) {
    try {
      return JSON.parse(el.getAttribute(attr) || 'null');
    } catch (e) {
      return null;
    }
  }

  /** Move dialogs to document.body so they never stay under #ProductInfo. */
  function promoteDialogsToBody() {
    qsa(document, 'dialog[data-bnr-modal]').forEach(function (dialog) {
      if (dialog.parentElement !== document.body) {
        document.body.appendChild(dialog);
      }
    });
  }

  /**
   * Light scroll lock — overflow only (no position:fixed).
   * Clears any leftover inline styles from older builds.
   */
  function lockBodyScroll() {
    if (scrollLockCount === 0) {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('bnr-modal-open');
      document.body.classList.add('bnr-modal-open');
    }
    scrollLockCount += 1;
  }

  function unlockBodyScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount !== 0) return;

    document.documentElement.classList.remove('bnr-modal-open');
    document.body.classList.remove('bnr-modal-open');

    // Purge older sticky-lock leftovers
    document.body.style.top = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    window.scrollTo(0, savedScrollY);
  }

  function estimateChestInches(heightIn, weightLb) {
    const bmi = (weightLb / (heightIn * heightIn)) * 703;
    let chest = heightIn * 0.5 + (bmi - 22) * 0.9 + 4;
    return Math.max(30, Math.min(58, chest));
  }

  function easeInches(pref) {
    if (pref === 'closer') return 1.5;
    if (pref === 'oversized') return 6;
    return 3.5;
  }

  function suggestSize(garment, heightIn, weightLb, fitPref) {
    if (!garment || !garment.sizes || !garment.sizes.length) return null;
    if (!garment.suggest_enabled) return null;
    const targetFlat = (estimateChestInches(heightIn, weightLb) + easeInches(fitPref)) / 2;
    const sizes = garment.sizes.slice();
    for (let i = 0; i < sizes.length; i++) {
      if (sizes[i].chest_width_in >= targetFlat) return sizes[i].size;
    }
    // No size reaches targetFlat — do not fall back to largest.
    return null;
  }

  function sizeLabel(size) {
    const map = {
      XS: 'XS',
      S: 'Small',
      M: 'Medium',
      L: 'Large',
      XL: 'XL',
      '2XL': '2XL',
      '3XL': '3XL',
      '4XL': '4XL',
      '5XL': '5XL',
    };
    const key = String(size).trim().toUpperCase();
    return map[key] || String(size);
  }

  function findSizeInput(size) {
    const want = String(size).trim().toUpperCase();
    const inputs = document.querySelectorAll(
      'variant-selects input[type="radio"], fieldset.product-form__input input[type="radio"]'
    );
    let matched = null;

    inputs.forEach(function (input) {
      const label = input.id ? document.querySelector('label[for="' + input.id + '"]') : null;
      const text = (label ? label.textContent : input.value || '').trim().toUpperCase();
      const value = (input.value || '').trim().toUpperCase();
      if (text !== want && value !== want) return;

      const fieldset = input.closest('fieldset');
      const legend = fieldset && fieldset.querySelector('legend');
      const legendText = legend ? legend.textContent.toLowerCase() : '';
      if (!matched || legendText.indexOf('size') !== -1) matched = input;
    });

    if (!matched) return null;

    let available = !matched.disabled && matched.getAttribute('disabled') === null;
    if (matched.hasAttribute('disabled')) available = false;
    const labelEl = matched.id
      ? document.querySelector('label[for="' + matched.id + '"]')
      : null;
    if (
      labelEl &&
      (labelEl.getAttribute('aria-disabled') === 'true' || labelEl.classList.contains('disabled'))
    ) {
      available = false;
    }
    return { input: matched, available: available };
  }

  function applySize(size) {
    const found = findSizeInput(size);
    if (!found) return { ok: false, reason: 'missing' };
    if (!found.available) return { ok: false, reason: 'unavailable', input: found.input };
    found.input.click();
    return { ok: true, input: found.input };
  }

  /**
   * Native <dialog> controller — uses showModal() top layer.
   * ESC / focus trap come from the browser; we restore focus + scroll.
   */
  function DialogController(dialog) {
    this.dialog = dialog;
    this.lastFocus = null;
    this._onClose = this._onClose.bind(this);
    this._onBackdrop = this._onBackdrop.bind(this);

    dialog.addEventListener('close', this._onClose);
    dialog.addEventListener('click', this._onBackdrop);
  }

  DialogController.prototype.isOpen = function () {
    return !!(this.dialog.open || this.dialog.hasAttribute('open'));
  };

  DialogController.prototype.open = function (trigger, opts) {
    opts = opts || {};
    this.lastFocus = trigger || document.activeElement;
    if (this.isOpen()) return;

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      // Very old browsers: still portal'd to body
      this.dialog.setAttribute('open', '');
      this.dialog.classList.add('bnr-dialog--fallback-open');
    }

    lockBodyScroll();
    if (!opts.preserveScroll) {
      const panel = qs(this.dialog, '.bnr-dialog__panel');
      if (panel) panel.scrollTop = 0;
    }
  };

  DialogController.prototype.close = function () {
    if (!this.isOpen()) return;
    if (typeof this.dialog.close === 'function' && this.dialog.open) {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
      this.dialog.classList.remove('bnr-dialog--fallback-open');
      this._onClose();
    }
  };

  DialogController.prototype._onClose = function () {
    unlockBodyScroll();
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      try {
        this.lastFocus.focus();
      } catch (e) {
        /* ignore */
      }
    }
  };

  DialogController.prototype._onBackdrop = function (e) {
    // Clicks on the dialog element itself (outside the panel) close it
    if (e.target === this.dialog) this.close();
  };

  function closeOpenDialog(name) {
    const d = qs(document, 'dialog[data-bnr-modal="' + name + '"]');
    if (d && d.open && typeof d.close === 'function') d.close();
  }

  let fmsTrigger = null;

  /**
   * Move Find my size button into the Size legend row (above pills).
   * Dawn replaces <variant-selects> on every option change, which destroys the
   * moved button, so we keep the node and re-insert it after each re-render.
   */
  function placeFmsTriggerBesideSize() {
    if (!fmsTrigger) fmsTrigger = qs(document, '[data-bnr-open-find-my-size]');
    const btn = fmsTrigger;
    if (!btn) return;
    if (btn.dataset.bnrPlaced === '1' && btn.isConnected && btn.closest('variant-selects')) return;

    const fieldsets = qsa(document, 'variant-selects fieldset.product-form__input--pill');
    let sizeFs = null;

    fieldsets.forEach(function (fs) {
      const leg = qs(fs, 'legend.form__label');
      if (!leg) return;
      const name = (leg.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (
        name === 'size' ||
        name.indexOf('size') === 0 ||
        name === 'talla' ||
        name.indexOf('talla') === 0
      ) {
        sizeFs = fs;
      }
    });

    if (!sizeFs && fieldsets.length) {
      sizeFs = fieldsets[fieldsets.length - 1];
    }
    if (!sizeFs) return;

    const legend = qs(sizeFs, 'legend.form__label');
    if (!legend) return;

    legend.classList.add('bnr-size-legend');
    const slot = btn.closest('[data-bnr-fms-trigger-slot]');
    legend.appendChild(btn);
    btn.dataset.bnrPlaced = '1';
    if (slot && slot.parentNode) slot.remove();
  }

  function watchVariantRerenders() {
    if (!fmsTrigger || fmsTrigger.dataset.bnrPlaced !== '1') return;
    let queued = false;
    new MutationObserver(function () {
      if (queued) return;
      if (fmsTrigger.isConnected && fmsTrigger.closest('variant-selects')) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        placeFmsTriggerBesideSize();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function initFitGuarantee() {
    const dialog = qs(document, 'dialog[data-bnr-modal="fit-guarantee"]');
    if (!dialog || dialog.dataset.bnrReady) return;
    dialog.dataset.bnrReady = '1';
    const ctl = new DialogController(dialog);
    const backBtn = qs(dialog, '[data-bnr-back-to-find-my-size]');
    let returnToFms = false;
    let fmsReturnFocus = null;

    function setReturnMode(on) {
      returnToFms = !!on;
      dialog.dataset.bnrReturnToFms = on ? '1' : '0';
      if (backBtn) backBtn.hidden = !on;
    }

    function openFit(trigger, fromFms) {
      setReturnMode(fromFms);
      if (fromFms) {
        fmsReturnFocus = trigger;
        const fms = qs(document, 'dialog[data-bnr-modal="find-my-size"]');
        if (fms && fms.open && typeof fms.close === 'function') {
          // Close without treating as final dismiss for focus restore noise
          fms.close();
        }
      } else {
        fmsReturnFocus = null;
        closeOpenDialog('find-my-size');
      }
      ctl.open(trigger);
    }

    qsa(document, '[data-bnr-open-fit-guarantee]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const fromFms = !!(
          btn.closest('dialog[data-bnr-modal="find-my-size"]') ||
          btn.closest('.bnr-fms-guarantee')
        );
        openFit(btn, fromFms);
      });
    });

    qsa(dialog, '[data-bnr-modal-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        setReturnMode(false);
        ctl.close();
      });
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        setReturnMode(false);
        ctl.close();
        const fms = qs(document, 'dialog[data-bnr-modal="find-my-size"]');
        if (!fms) return;
        // Re-open FMS; form values / result / tab stay in the closed dialog DOM
        if (typeof fms.showModal === 'function') {
          fms.showModal();
          lockBodyScroll();
        } else {
          fms.setAttribute('open', '');
          fms.classList.add('bnr-dialog--fallback-open');
          lockBodyScroll();
        }
        if (fmsReturnFocus && typeof fmsReturnFocus.focus === 'function') {
          try {
            fmsReturnFocus.focus();
          } catch (e) {
            /* ignore */
          }
        }
      });
    }

    // If Fit closes via ESC while in return mode, do not auto-reopen FMS
    // (X / ESC = exit the whole flow). Clear the flag.
    dialog.addEventListener('close', function () {
      if (backBtn) backBtn.hidden = true;
      returnToFms = false;
      dialog.dataset.bnrReturnToFms = '0';
    });
  }

  function renderNoConfidentMatch(resultEl, setMode) {
    resultEl.hidden = false;
    resultEl.classList.add('bnr-fms-result--no-match');
    resultEl.innerHTML =
      '<p class="bnr-fms-result__eyebrow">WE COULDN’T CONFIDENTLY MATCH A SIZE</p>' +
      '<p class="bnr-fms-result__note bnr-fms-result__note--lead">' +
      'Our available sizes don’t quite reach the fit estimate from the information you entered.</p>' +
      '<p class="bnr-fms-result__note">' +
      'For the best fit, compare this garment’s measurements with a shirt you already own and love.</p>' +
      '<div class="bnr-fms-result__actions">' +
      '<button type="button" class="bnr-btn" data-bnr-goto-compare>COMPARE MEASUREMENTS</button>' +
      '</div>';

    const goto = qs(resultEl, '[data-bnr-goto-compare]');
    if (goto) {
      goto.addEventListener('click', function () {
        setMode('compare');
      });
    }
  }

  function renderSuggestionResult(resultEl, size, setMode, ctl) {
    resultEl.classList.remove('bnr-fms-result--no-match');
    const found = findSizeInput(size);
    let actions = '<div class="bnr-fms-result__actions">';
    let warn = '';

    if (!found) {
      warn =
        '<p class="bnr-fms-result__warn">' +
        sizeLabel(size) +
        ' is our suggested fit, but this product doesn’t list that size.</p>';
    } else if (!found.available) {
      warn =
        '<p class="bnr-fms-result__warn">' +
        sizeLabel(size) +
        ' is our suggested fit, but it’s currently unavailable.</p>';
    } else {
      actions +=
        '<button type="button" class="bnr-btn" data-bnr-apply-size="' +
        size +
        '">SELECT SIZE ' +
        String(size).toUpperCase() +
        '</button>';
    }

    actions +=
      '<button type="button" class="bnr-btn bnr-btn--secondary" data-bnr-goto-compare>Compare measurements</button></div>';

    resultEl.hidden = false;
    resultEl.innerHTML =
      '<p class="bnr-fms-result__eyebrow">YOUR SUGGESTED SIZE</p>' +
      '<p class="bnr-fms-result__size" aria-live="polite">' +
      String(size).toUpperCase() +
      '</p>' +
      '<p class="bnr-fms-result__note">Based on your measurements and preferred fit.</p>' +
      warn +
      actions;

    const goto = qs(resultEl, '[data-bnr-goto-compare]');
    if (goto) {
      goto.addEventListener('click', function () {
        setMode('compare');
      });
    }
    const applyBtn = qs(resultEl, '[data-bnr-apply-size]');
    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        const result = applySize(size);
        if (result.ok) {
          ctl.close();
          return;
        }
        if (result.reason === 'unavailable') {
          resultEl.querySelectorAll('[data-bnr-apply-size]').forEach(function (b) {
            b.remove();
          });
          const warn = document.createElement('p');
          warn.className = 'bnr-fms-result__warn';
          warn.textContent =
            sizeLabel(size) + ' is our suggested fit, but it’s currently unavailable.';
          resultEl.appendChild(warn);
        }
      });
    }
  }

  function initFindMySize(root) {
    const wrap = root || document.querySelector('[data-bnr-fms]');
    if (!wrap || wrap.dataset.bnrFmsReady) return;
    wrap.dataset.bnrFmsReady = '1';

    const garment = parseJSON(wrap, 'data-garment');
    if (!garment || !garment.sizes || !garment.sizes.length) return;

    // Dialog may already be on document.body after promote
    const dialog =
      qs(document, 'dialog[data-bnr-modal="find-my-size"]') ||
      qs(wrap, 'dialog[data-bnr-modal="find-my-size"]');
    if (!dialog) return;
    const ctl = new DialogController(dialog);

    qsa(document, '[data-bnr-open-find-my-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        ctl.open(btn);
      });
    });
    qsa(dialog, '[data-bnr-modal-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        ctl.close();
      });
    });

    const tabs = qsa(dialog, '[data-bnr-fms-tab]');
    const panels = {
      suggest: qs(dialog, '[data-bnr-fms-panel="suggest"]'),
      compare: qs(dialog, '[data-bnr-fms-panel="compare"]'),
    };

    function setMode(mode) {
      tabs.forEach(function (tab) {
        if (tab.hidden) return;
        tab.setAttribute(
          'aria-selected',
          tab.getAttribute('data-bnr-fms-tab') === mode ? 'true' : 'false'
        );
      });
      Object.keys(panels).forEach(function (key) {
        if (!panels[key]) return;
        panels[key].hidden = key !== mode;
      });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        setMode(tab.getAttribute('data-bnr-fms-tab'));
      });
    });

    qsa(dialog, '[data-bnr-goto-compare]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setMode('compare');
      });
    });
    qsa(dialog, '[data-bnr-goto-suggest]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setMode('suggest');
      });
    });

    let unitSystem = 'imperial';
    let measureUnit = 'in';
    const unitSeg = qsa(dialog, '[data-bnr-unit-system]');
    const heightImperial = qs(dialog, '[data-bnr-height-imperial]');
    const heightMetric = qs(dialog, '[data-bnr-height-metric]');
    const weightInput = qs(dialog, '[data-bnr-weight]');
    const weightLabel = qs(dialog, '[data-bnr-weight-label]');

    function syncUnitUI() {
      unitSeg.forEach(function (btn) {
        btn.setAttribute(
          'aria-pressed',
          btn.getAttribute('data-bnr-unit-system') === unitSystem ? 'true' : 'false'
        );
      });
      if (heightImperial) heightImperial.hidden = unitSystem !== 'imperial';
      if (heightMetric) heightMetric.hidden = unitSystem !== 'metric';
      if (weightLabel) {
        weightLabel.textContent =
          unitSystem === 'imperial' ? 'Weight (lb)' : 'Weight (kg)';
      }
      if (weightInput) {
        weightInput.setAttribute(
          'aria-label',
          unitSystem === 'imperial' ? 'Weight in pounds' : 'Weight in kilograms'
        );
      }
    }

    unitSeg.forEach(function (btn) {
      btn.addEventListener('click', function () {
        unitSystem = btn.getAttribute('data-bnr-unit-system');
        syncUnitUI();
      });
    });
    syncUnitUI();

    let fitPref = 'relaxed';
    qsa(dialog, '[data-bnr-fit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        fitPref = btn.getAttribute('data-bnr-fit');
        qsa(dialog, '[data-bnr-fit]').forEach(function (b) {
          b.setAttribute(
            'aria-pressed',
            b.getAttribute('data-bnr-fit') === fitPref ? 'true' : 'false'
          );
        });
      });
    });

    const resultEl = qs(dialog, '[data-bnr-fms-result]');
    const suggestBtn = qs(dialog, '[data-bnr-find-size]');

    function readHeightWeight() {
      if (unitSystem === 'imperial') {
        const ftEl = qs(dialog, '[data-bnr-ft]');
        const inEl = qs(dialog, '[data-bnr-in]');
        const ft = parseFloat(ftEl && ftEl.value);
        const inch = parseFloat(inEl && inEl.value);
        const lb = parseFloat(weightInput && weightInput.value);
        if (!isFinite(ft) || !isFinite(inch) || !isFinite(lb)) return null;
        if (ft < 3 || ft > 8 || inch < 0 || inch >= 12 || lb < 50 || lb > 500) return null;
        return { heightIn: ft * 12 + inch, weightLb: lb };
      }
      const cmEl = qs(dialog, '[data-bnr-cm]');
      const cm = parseFloat(cmEl && cmEl.value);
      const kg = parseFloat(weightInput && weightInput.value);
      if (!isFinite(cm) || !isFinite(kg)) return null;
      if (cm < 100 || cm > 250 || kg < 25 || kg > 250) return null;
      return { heightIn: cm / IN_TO_CM, weightLb: kg / LB_TO_KG };
    }

    if (suggestBtn) {
      suggestBtn.addEventListener('click', function () {
        if (!resultEl) return;
        if (!garment.suggest_enabled) {
          resultEl.classList.remove('bnr-fms-result--no-match');
          resultEl.hidden = false;
          resultEl.innerHTML =
            '<p class="bnr-fms-result__error">Size suggestion isn’t available for this garment yet. Please compare measurements with a shirt you already own.</p>';
          return;
        }
        const hw = readHeightWeight();
        if (!hw) {
          resultEl.classList.remove('bnr-fms-result--no-match');
          resultEl.hidden = false;
          resultEl.innerHTML =
            '<p class="bnr-fms-result__error">Please enter a valid height and weight to see a suggestion.</p>';
          return;
        }
        const size = suggestSize(garment, hw.heightIn, hw.weightLb, fitPref);
        if (!size) {
          if (garment.suggest_enabled && garment.sizes && garment.sizes.length) {
            renderNoConfidentMatch(resultEl, setMode);
          } else {
            resultEl.classList.remove('bnr-fms-result--no-match');
            resultEl.hidden = false;
            resultEl.innerHTML =
              '<p class="bnr-fms-result__error">We don’t have enough sizing data for this garment to suggest a size.</p>';
          }
          return;
        }
        renderSuggestionResult(resultEl, size, setMode, ctl);
      });
    }

    const measureSeg = qsa(dialog, '[data-bnr-measure-unit]');
    const tableBody = qs(dialog, '[data-bnr-measure-body]');

    function renderTable() {
      if (!tableBody) return;
      const factor = measureUnit === 'cm' ? IN_TO_CM : 1;
      const decimals = measureUnit === 'cm' ? 1 : 2;
      tableBody.innerHTML = garment.sizes
        .map(function (row) {
          return (
            '<tr><td>' +
            row.size +
            '</td><td>' +
            (row.chest_width_in * factor).toFixed(decimals) +
            '</td><td>' +
            (row.body_length_in * factor).toFixed(decimals) +
            '</td></tr>'
          );
        })
        .join('');
    }

    measureSeg.forEach(function (btn) {
      btn.addEventListener('click', function () {
        measureUnit = btn.getAttribute('data-bnr-measure-unit');
        measureSeg.forEach(function (b) {
          b.setAttribute(
            'aria-pressed',
            b.getAttribute('data-bnr-measure-unit') === measureUnit ? 'true' : 'false'
          );
        });
        renderTable();
      });
    });
    renderTable();

    if (!garment.suggest_enabled) {
      const suggestTab = qs(dialog, '[data-bnr-fms-tab="suggest"]');
      if (suggestTab) suggestTab.hidden = true;
      setMode('compare');
    }

    wrap._bnrFms = {
      suggestSize: function (h, w, fit) {
        return suggestSize(garment, h, w, fit || 'relaxed');
      },
      applySize: applySize,
      findSizeInput: findSizeInput,
      garment: garment,
      dialog: dialog,
    };
  }

  function boot() {
    promoteDialogsToBody();
    placeFmsTriggerBesideSize();
    watchVariantRerenders();
    initFitGuarantee();
    document.querySelectorAll('[data-bnr-fms]').forEach(function (el) {
      initFindMySize(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
